import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import UserAdd from './crudModals/UserAdd';
import { usePageNavigation } from 'components/navigation/NavigationContext';
import RightSidebar from 'components/navigation/RightSidebar';

const FILTERS = [
  { key: 'Officer', label: 'Officers', icon: 'bi bi-star-fill' },
  { key: 'Member', label: 'Members', icon: 'bi bi-person-fill' },
  { key: 'Guest Member', label: 'Guest Members', icon: 'bi bi-person-badge' },
  { key: 'Guest', label: 'Guests', icon: 'bi bi-person' },
  { key: '', label: 'All', icon: 'bi bi-people-fill' },
];

export default function UsersToolbar({
  menuOpen,
  setMenuOpen,
  searchTerm,
  onSearchChange,
  selectedRole,
  onRoleChange,
  getUsers,
  onOpenStats,
}) {
  const { user: authUser } = useSelector((state) => state.auth);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  usePageNavigation({
    preferredOrientation: 'portrait',
    primaryAction: authUser?.user?.acl === 'Officer' ? {
      label: 'Add Member', icon: 'bi-person-plus', onClick: () => setShowAddUserModal(true),
    } : null,
  });
  const toggleRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;

    function onPointerDown(e) {
      if (
        e.target instanceof Element &&
        (e.target.closest('.modal.show') || e.target.closest('.modal-backdrop.show'))
      ) {
        return;
      }
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (toggleRef.current && toggleRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, setMenuOpen]);

  if (!authUser?.user) return null;

  return (
    <>
      <div ref={toggleRef} className="right-menu">
        <button
          className="right-menu-toggle"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle users menu"
          title="Users options"
        >
          <i className="bi bi-sliders2"></i>
        </button>
      </div>

      <RightSidebar label="Users" open={menuOpen} onClose={() => setMenuOpen(false)} panelRef={panelRef}>
            <div className="right-menu-header">
              <span className="right-menu-title">Users</span>
              <button className="right-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>

            <div className="right-menu-items">
              {/* Search */}
              <div className="right-menu-search">
                <i className="bi bi-search right-menu-search-icon"></i>
                <input
                  type="text"
                  className="right-menu-search-input"
                  placeholder="Search users…"
                  value={searchTerm}
                  onChange={e => onSearchChange(e.target.value)}
                  autoFocus
                />
                {searchTerm && (
                  <button
                    className="right-menu-search-clear"
                    onClick={() => onSearchChange('')}
                    aria-label="Clear search"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>

              <hr className="right-menu-divider" />

              {/* Add User — only officers can add */}
              {authUser.user.acl === 'Officer' && (
                <button
                  className="right-menu-item right-menu-item--add"
                  data-mobile-modal
                  onClick={() => {
                    setShowAddUserModal(true);
                    setMenuOpen(false);
                  }}
                >
                  <i className="bi bi-person-plus"></i>
                  <span>Add User</span>
                </button>
              )}

              {/* Membership type filters */}
              {FILTERS.map(f => (
                <button
                  key={f.key}
                  className={`right-menu-item${selectedRole === f.key ? ' active' : ''}`}
                  onClick={() => { onRoleChange(f.key); setMenuOpen(false); }}
                >
                  <i className={f.icon}></i>
                  <span>{f.label}</span>
                </button>
              ))}

              <hr className="right-menu-divider" />

              {/* Stats */}
              <button
                className="right-menu-item"
                data-mobile-modal
                onClick={() => {
                  if (onOpenStats) onOpenStats();
                  setMenuOpen(false);
                }}
              >
                <i className="bi bi-bar-chart-fill"></i>
                <span>Stats</span>
              </button>
            </div>
      </RightSidebar>

      <UserAdd
        navigationParent="configuration"
        getUsers={getUsers}
        triggerMode="none"
        showModalOverride={showAddUserModal}
        setShowModalOverride={setShowAddUserModal}
      />

    </>
  );
}
